import { ApolloClient } from "apollo-client"
import { InMemoryCache } from "apollo-cache-inmemory"
import { createHttpLink } from "apollo-link-http"
import { setContext } from "apollo-link-context"
import { onError } from "apollo-link-error"
import { ApolloLink, Observable } from "apollo-link"

// This one is defined by webpack's DefinePlugin
// API_HOST should (by now) end with a /
const GRAPHQL_ENDPOINT = `${API_HOST}graphql`

// Session JWT is stored by loginService (REST) under localStorage key "token".
import Auth from "@/auth.js"
import {
  isGraphQLAuthError,
  isIdentityAuthMode,
  refreshAccessToken
} from "@/identity-token-refresh.js"

// Configure apollo links
const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
  credentials: "same-origin"
})

const authLink = setContext((_, { headers }) => {
  // get the authentication token from memory if it exists
  const token = Auth.getToken()
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      Authorization: token ? `JWT ${token}` : ""
    }
  }
})

/**
 * Apollo error link that transparently retries an operation once, after
 * refreshing the Identity access token.
 */
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    )
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`)
  }

  if (
    !graphQLErrors ||
    !isGraphQLAuthError(graphQLErrors) ||
    !isIdentityAuthMode() ||
    operation.getContext()._identityRetried
  ) {
    return
  }

  return new Observable(function(observer) {
    var retrySubscription = null
    var unsubscribed = false

    refreshAccessToken()
      .then(function(token) {
        if (unsubscribed) {
          return
        }

        operation.setContext(function(context) {
          return {
            ...context,
            _identityRetried: true,
            headers: {
              ...context.headers,
              Authorization: `JWT ${token}`
            }
          }
        })

        retrySubscription = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer)
        })
      })
      .catch(function(error) {
        if (unsubscribed) {
          return
        }

        observer.error(error)
      })

    return function teardown() {
      unsubscribed = true

      if (retrySubscription) {
        retrySubscription.unsubscribe()
        retrySubscription = null
      }
    }
  })
})

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache()
})

export default client
